// A building's on-site survey history, inside Building readiness's survey area: getting on
// site, the version relied on and who accepted it, each version's findings, the feedback
// each party owes and every exchange, latest first. The state and tiles come from the
// backend (survey_history.card_payload). See docs/Y2026-Q4/design-roof-survey-history.md.
import type {
  SurveyHistory,
  SurveyReport,
} from "../api/hooks/useProjectAgentStatus";
import { formatDay } from "../utils/attributeFormat";
import styles from "./EmailHistory.module.css";
import {
  Acceptances,
  BriefLine,
  ExchangesSection,
  FeedbackSection,
  FeedbackTile,
  StateLine,
  Tile,
  type Cite,
} from "./ReadinessHistoryParts";

const SOURCE_LABEL: Record<SurveyReport["source"], string> = {
  ours: "Our installer or surveyor",
  client: "Client's surveyor",
  independent: "Independent surveyor",
};

const REACHED_LABEL: Record<SurveyReport["access"]["reached_by"], string> = {
  internal_stairs: "Internal stairs",
  hatch: "Roof hatch",
  ladder: "Ladder",
  mewp: "MEWP",
  drone_only: "Drone only",
  not_stated: "Access not stated",
};

const EXTENT_LABEL: Record<SurveyReport["area_surveyed"]["extent"], string> = {
  whole_roof: "Whole roof",
  pv_zones: "PV zones only",
  part: "Part of the roof",
  not_stated: "Area not stated",
};

const AREA_NAME: Record<SurveyHistory["access"][number]["area"], string> = {
  structural: "structural",
  roof_condition: "roof",
  on_site_survey: "survey",
};

const Stated = ({ text }: { text: string }) =>
  text ? <>{text}</> : <span className={styles.notRecorded}>Not stated</span>;

export const SurveyHistoryCard = ({
  history,
  cite,
}: {
  history: SurveyHistory;
  cite: Cite;
}) => {
  const visits = history.access.filter((a) => a.kind === "visited");
  const latest = history.access[history.access.length - 1];
  return (
    <>
      <StateLine state={history.state} />
      <div className={`${styles.stages} ${styles.tiles}`}>
        <Tile
          label="Access"
          main={visits.length ? `Visited ${formatDay(visits[0].date)}` : ""}
          empty={latest ? latest.kind_label : "Nothing logged"}
          sub={
            visits.length
              ? <BriefLine brief={visits[0]} cite={cite} />
              : latest && `${formatDay(latest.date)}: ${latest.summary}`
          }
        />
        <Tile
          label="Report relied on"
          main={history.relied_on && formatDay(history.relied_on.date)}
          empty="No complete version yet"
          sub={
            history.relied_on && (
              <>
                {history.relied_on.version}, {cite(history.relied_on.email)}
              </>
            )
          }
        />
        <Tile
          label="Accepted"
          main={history.acceptance && formatDay(history.acceptance.date)}
          empty={
            history.newer
              ? `${history.newer.version} not accepted yet`
              : "Not accepted"
          }
          sub={history.acceptance && <BriefLine brief={history.acceptance} cite={cite} />}
        />
        <FeedbackTile history={history} />
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionLabel}>Getting on site</h4>
        {history.access.length === 0 ? (
          <p className={styles.empty}>No access request or visit logged yet.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>What</th>
                  <th>From</th>
                  <th>What it shows</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {[...history.access].reverse().map((a) => (
                  <tr key={a.entry_id}>
                    <td className={styles.date}>{formatDay(a.date)}</td>
                    <td>
                      <span className={`${styles.tag} ${styles.access}`}>
                        {a.kind_label}
                      </span>
                    </td>
                    <td>{a.sender}</td>
                    <td className={styles.summary}>
                      {a.summary}
                      {a.area !== "on_site_survey" && (
                        <span className={styles.note}>
                          Logged in the {AREA_NAME[a.area]} history
                        </span>
                      )}
                    </td>
                    <td>{cite(a.email)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionLabel}>Report versions</h4>
        {history.versions.length === 0 ? (
          <p className={styles.empty}>No survey report logged yet.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Issued</th>
                  <th>Version</th>
                  <th>Access and area surveyed</th>
                  <th>Exclusion zones</th>
                  <th>Cable routes and equipment</th>
                  <th>DNO position</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {history.versions.map((v) => (
                  <tr key={v.entry_id}>
                    <td
                      className={`${styles.date} ${v.relied_on ? styles.first : ""}`}
                    >
                      {formatDay(v.date)}
                    </td>
                    <td>
                      <strong>
                        {v.report.firm} {v.report.revision}
                      </strong>
                      {v.report.status === "draft" && (
                        <span className={`${styles.tag} ${styles.other}`}>
                          {" "}
                          Draft
                        </span>
                      )}
                      <span className={styles.note}>
                        {SOURCE_LABEL[v.report.source]}
                        {v.report.visits.length > 0 &&
                          ` · visit ${v.report.visits.map(formatDay).join(", ")}`}
                      </span>
                      {v.relied_on && (
                        <span className={styles.note}>Relied on</span>
                      )}
                      <Acceptances accepted={v.accepted} />
                      {v.report.document && (
                        <span className={styles.note}>
                          {cite(v.report.document)}
                        </span>
                      )}
                    </td>
                    <td>
                      {REACHED_LABEL[v.report.access.reached_by]}
                      {v.report.access.unseen && (
                        <span className={styles.note}>
                          Unseen: {v.report.access.unseen}
                        </span>
                      )}
                      <span className={styles.note}>
                        {EXTENT_LABEL[v.report.area_surveyed.extent]}
                        {v.report.area_surveyed.excluded &&
                          `, excluding ${v.report.area_surveyed.excluded}`}
                      </span>
                    </td>
                    <td className={styles.summary}>
                      {v.report.exclusion_zones.length === 0 ? (
                        <span className={styles.notRecorded}>None stated</span>
                      ) : (
                        <ul className={styles.points}>
                          {v.report.exclusion_zones.map((z, i) => (
                            <li key={i}>
                              {z.zone}
                              {z.reason && ` (${z.reason})`}
                              {z.set_by && (
                                <span className={styles.note}>
                                  Set by {z.set_by}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>
                      <Stated text={v.report.cable_routes} />
                      {v.report.equipment && (
                        <span className={styles.note}>
                          {v.report.equipment}
                        </span>
                      )}
                    </td>
                    <td>
                      <Stated text={v.report.dno_position} />
                      {v.report.open_questions.length > 0 && (
                        <span className={styles.note}>
                          Open: {v.report.open_questions.join("; ")}
                        </span>
                      )}
                    </td>
                    <td>{cite(v.email)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <FeedbackSection history={history} cite={cite} />
      <ExchangesSection
        entries={history.entries}
        cite={cite}
        empty="Building readiness has not logged any message about the survey yet."
      />
    </>
  );
};
